import { threadsApi } from '@/api/threads.api';
import type { ThreadSpecModel } from '@/types/thread.types';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

/**
 * Order the table the way the thread chart is read: by nominal diameter, and within one
 * diameter the coarse pitch before the fine one.
 *
 * Standard pitch is always the larger of the two for a given diameter, so sorting pitch
 * descending puts `standard` ahead of `fine` without special-casing `thread_type`.
 * `designation` breaks the remaining ties so the order is stable across reloads.
 */
function sortSpecs(specs: ThreadSpecModel[]): ThreadSpecModel[] {
  return [...specs].sort((a, b) => {
    if (a.nominal_diameter_mm !== b.nominal_diameter_mm) return a.nominal_diameter_mm - b.nominal_diameter_mm;
    if (a.pitch !== b.pitch) return b.pitch - a.pitch;
    return a.designation.localeCompare(b.designation, 'cs');
  });
}

/**
 * Load the thread table, already sorted for display.
 *
 * The backend caches the list for five minutes and invalidates on write, so `refetch` after
 * an import or a delete-all returns fresh rows rather than the pre-write snapshot. It also
 * hands those rows back to the caller, so a page that wants to compare the table before and
 * after a write does not have to wait for the state update to land.
 */
export const useThreadSpecs = () => {
  // `i18n.t` rather than `t`: the i18n instance is referentially stable, so the fetch
  // callback does not change identity on every language switch and re-trigger the request.
  const { i18n } = useTranslation();
  const [specs, setSpecs] = useState<ThreadSpecModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSpecs = useCallback(async (): Promise<ThreadSpecModel[]> => {
    setIsLoading(true);
    setError(null);
    try {
      const sorted = sortSpecs(await threadsApi.getAll());
      setSpecs(sorted);
      return sorted;
    } catch (err) {
      console.error('Failed to load the thread table:', err);
      const message = i18n.t('threads.loadFailed');
      setError(message);
      toast.error(message);
      // The toast and `error` already say the load failed; an empty list keeps every caller
      // on the success path without a second error channel to handle.
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [i18n]);

  useEffect(() => {
    fetchSpecs();
  }, [fetchSpecs]);

  return { specs, isLoading, error, refetch: fetchSpecs };
};
